import type { ReactNode } from 'react'

import startCase from 'lodash/startCase'

import { AlarmCodeFilter } from '@/components/ui/alarm-code-filter'
import { useAlarmCodesForSource } from '@/hooks'
import { collapseAllCodeIds, expandAllCodeIds } from '@/insights/common/alarmCodeSelection'
import { InsightConfigControl } from '@/types/dashboard-types'
import { DATA_SOURCE_KEY, DEFAULT_DATA_SOURCE, type DataSource } from '@/types/data-source'

import { AlertNameDropdown } from './AlertNameDropdown'
import { AssetDropdownMulti } from './AssetDropdownMulti'
import { AssetDropdownSingle } from './AssetDropdownSingle'
import { AssetMakeDropdown } from './AssetMakeDropdown'
import { CompanyDropdown } from './CompanyDropdown'
import { CustomMultiSelect } from './CustomMultiSelect'
import CustomNumberInput from './CustomNumberInput'
import { CustomSingleSelect } from './CustomSingleSelect'
import { DateSelect } from './DateSelect'
import { DateTimeRange } from './DateTimeRange'
import { FacilityDropdownSingle } from './FacilityDropdownSingle'
import { GeofenceCategoryDropdown } from './GeofenceCategoryDropdown'
import { GeofenceDropdown } from './GeofenceDropdown'
import { Label } from './Label'
import { SeverityDropdown } from './SeverityDropdown'
import SpeedInput from './SpeedInput'
import TemperatureInput from './TemperatureInput'
import type { AssetDateRange } from './useAssetCurrentStatus'

type Range = { from?: string; to?: string }
export type ControlValues = string | number | Range

interface Params<P> {
  control: InsightConfigControl<P>
  parameters: P
  onChange: (params: P) => void
  disabledFieldLabels?: Record<string, string>
}

export const ConfigControls = <P,>({ control, parameters, onChange, disabledFieldLabels }: Params<P>): ReactNode => {
  // Every reference list this panel offers has to come from the same back end the insight
  // will query, or the ids in the dropdown won't be the ids the data is keyed by.
  const dataSource = (parameters as Record<string, unknown>)[DATA_SOURCE_KEY] as DataSource | undefined
  const disabledLabel = disabledFieldLabels?.[control.id as string]
  if (disabledLabel !== undefined) {
    return (
      <div>
        <label className="block text-base-content mb-1">{control.label || startCase(control.id as string)}</label>
        <div className="text-sm bg-base-200 rounded px-3 py-2 border border-base-300">
          <span className="text-base-content">{disabledLabel}</span>
          <span className="ml-2 text-xs italic text-info">(from dashboard)</span>
        </div>
      </div>
    )
  }

  if (control.dependsOn) {
    const value = parameters[control.dependsOn as keyof P]
    if (control.dependsOnValue) {
      if (value !== control.dependsOnValue) {
        return
      }
    }
    if (!value) {
      return
    }
  }

  if (control.control === 'CompanyDropdown') {
    const { companyId, companyIds } = parameters as { companyId: number; companyIds: number[] }
    const { isMultiSelect, acknowledgementOnly } = control as InsightConfigControl<P>
    const selectedValues = isMultiSelect ? companyIds : companyId
    return (
      <CompanyDropdown
        value={selectedValues ?? undefined}
        onChange={(value) =>
          onChange({
            ...parameters,
            ...(isMultiSelect ? { companyIds: value as number[] } : { companyId: value as number }),
          })
        }
        isMultiSelect={isMultiSelect}
        filter={acknowledgementOnly ? (c) => !!c.enabledAlertAcknowledgement : undefined}
        dataSource={dataSource}
      />
    )
  }
  if (control.control === 'AlarmCodeDropdown') {
    const alarmCodesRaw = useAlarmCodesForSource(dataSource ?? DEFAULT_DATA_SOURCE)
    const field = control.id || 'codeId'
    const allCodeIds = alarmCodesRaw.map((code) => code.id)
    const selectedCodeIds = expandAllCodeIds(parameters[field as keyof P] as number[] | undefined, allCodeIds)

    const alarmCodes = alarmCodesRaw.map((code) => ({
      id: code.id,
      code: code.code.toString(),
      description: code.description,
      severityId: code.severityId,
    }))

    return (
      <div>
        {control.label && <label className="block text-base-content mb-2">{control.label}</label>}

        <AlarmCodeFilter
          alarmCodes={alarmCodes}
          selectedCodeIds={selectedCodeIds}
          onChange={(codeIds) => {
            if (codeIds.length > 0) {
              onChange({ ...parameters, [field]: collapseAllCodeIds(codeIds, allCodeIds) })
            } else {
              const { [field]: _, ...rest } = parameters
              onChange(rest as P)
            }
          }}
          showSeverityCheckboxes={false}
          showNoAlarmsCheckbox={false}
          renderMode="inline"
        />
      </div>
    )
  }

  if (control.control === 'AlertNameDropdown') {
    const { alertConfigIds, companyId } = parameters as { companyId: number; alertConfigIds: string[] }
    const { acknowledgementOnly, disableUserSubscriptionFilter } = control as InsightConfigControl<P>
    return (
      <AlertNameDropdown
        companyId={companyId}
        alertConfigIds={alertConfigIds ?? undefined}
        acknowledgementOnly={acknowledgementOnly}
        disableUserSubscriptionFilter={disableUserSubscriptionFilter}
        onChange={(alertConfigIds) => onChange({ ...parameters, alertConfigIds })}
      />
    )
  }

  if (control.control === 'GeofenceDropdown') {
    const { geofenceIds, companyIds, companyId } = parameters as {
      companyIds: number[]
      geofenceIds: string[]
      companyId: number
    }
    return (
      <GeofenceDropdown
        companyIds={companyIds ?? (companyId ? [companyId] : undefined)}
        geofenceIds={geofenceIds ?? undefined}
        onChange={(geofenceIds) => onChange({ ...parameters, geofenceIds })}
        dataSource={dataSource}
      />
    )
  }

  if (control.control === 'GeofenceCategoryDropdown') {
    const { geofenceCategoryIds, companyIds, companyId } = parameters as {
      companyIds: number[]
      geofenceCategoryIds: string[]
      companyId: number
    }
    return (
      <GeofenceCategoryDropdown
        companyIds={companyIds ?? (companyId ? [companyId] : undefined)}
        geofenceCategoryIds={geofenceCategoryIds ?? undefined}
        onChange={(geofenceCategoryIds) => onChange({ ...parameters, geofenceCategoryIds })}
      />
    )
  }

  if (control.control === 'CustomNumberInput') {
    const field = control.id
    const defaultValue = control.defaultValue as number | undefined
    const { max, placeholder, options, showDefault } = control as InsightConfigControl<P> & {
      max?: number
      placeholder?: string
      options?: { label: string; value: string; isDefault?: boolean }[]
    }
    const value = showDefault
      ? ((parameters[field] as string | number) ?? defaultValue)
      : (parameters[field] as string | number)
    return (
      <CustomNumberInput
        label={control.label || startCase(String(control.id))}
        value={value}
        defaultValue={defaultValue}
        onChange={(value) => onChange({ ...parameters, [field]: value })}
        max={max}
        placeholder={placeholder}
        units={options}
      />
    )
  }

  if (control.control === 'SpeedInput') {
    const field = control.id
    const defaultValue = control.defaultValue as number | undefined
    const { min, max, placeholder } = control as InsightConfigControl<P> & {
      min?: number
      max?: number
      placeholder?: string
    }
    return (
      <SpeedInput
        label={control.label || startCase(String(control.id))}
        value={parameters[field] as string | number}
        defaultValue={defaultValue}
        onChange={(value) => onChange({ ...parameters, [field]: value })}
        min={min}
        max={max}
        placeholder={placeholder}
      />
    )
  }

  if (control.control === 'TemperatureInput') {
    const field = control.id
    const defaultValue = control.defaultValue as number | undefined
    const { min, max, placeholder } = control as InsightConfigControl<P> & {
      min?: number
      max?: number
      placeholder?: string
    }
    return (
      <TemperatureInput
        label={control.label || startCase(String(control.id))}
        value={parameters[field] as string | number}
        defaultValue={defaultValue}
        onChange={(value) => onChange({ ...parameters, [field]: value })}
        min={min}
        max={max}
        placeholder={placeholder}
      />
    )
  }

  if (control.control === 'AssetDropdownMulti') {
    const { assetIds, companyId } = parameters as {
      companyId: number
      assetIds: string[]
    }
    return (
      <AssetDropdownMulti
        companyId={companyId}
        assetIds={assetIds ?? undefined}
        includeSelectAll={control.includeSelectAll}
        assetType={control.assetType}
        onChange={(assetIds) => onChange({ ...parameters, assetIds })}
        dataSource={dataSource}
        dateRange={parameters as AssetDateRange}
      />
    )
  }

  if (control.control === 'AssetDropdownSingle') {
    const { assetId, companyId, facilityId } = parameters as {
      assetId: string
      companyId: number
      facilityId?: string
    }
    return (
      <AssetDropdownSingle
        companyId={companyId}
        facilityId={facilityId}
        asset={assetId ?? undefined}
        assetType={control.assetType}
        onChange={(assetId) => onChange({ ...parameters, assetId })}
        label={control.label}
        dataSource={dataSource}
        dateRange={parameters as AssetDateRange}
      />
    )
  }

  if (control.control === 'FacilityDropdownSingle') {
    const field = control.id
    const { companyId } = parameters as { companyId: number }
    return (
      <FacilityDropdownSingle
        companyId={companyId}
        facility={parameters[field] as string | undefined}
        onChange={(facilityId) => onChange({ ...parameters, [field]: facilityId })}
        label={control.label}
        dataSource={dataSource}
      />
    )
  }

  if (control.control === 'AssetMakeDropdown') {
    const { companyId } = parameters as { companyId: number }
    const makes = parameters[control.id || ('makes' as keyof P)] as string[]
    return (
      <AssetMakeDropdown
        label={control.label}
        placeholder={control.placeholder}
        companyId={companyId}
        makes={makes ?? undefined}
        onChange={(makes) =>
          onChange({ ...parameters, make: undefined, makes: undefined, [control.id || 'makes']: makes })
        }
        dataSource={dataSource}
      />
    )
  }

  if (control.control === 'SeverityDropdown') {
    const values = (parameters as { severity: string[] }).severity
    return <SeverityDropdown values={values} onChange={(severity) => onChange({ ...parameters, severity })} />
  }

  if (control.control === 'DateSelect') {
    const fieldId = (control.id || 'date') as keyof P
    const date = parameters[fieldId] as string | undefined
    return (
      <DateSelect
        date={date}
        onChange={({ date }) => {
          onChange({ ...parameters, [fieldId]: date })
        }}
        help={control.help}
        label={control.label}
        showTime={control.showTime}
      />
    )
  }
  if (control.control === 'DateTimeRange') {
    const { from, to } = parameters as { from?: string; to?: string }
    return (
      <DateTimeRange
        from={from}
        to={to}
        onChange={({ from, to }) => {
          onChange({ ...parameters, from, to })
        }}
        help={control.help}
      />
    )
  }
  if (control.control === 'CustomMultiSelect') {
    const fieldId = control.id as keyof P
    const values = parameters[fieldId]
    const options = control.options!
    return (
      <CustomMultiSelect
        values={values as string[]}
        label={control.label || startCase(control.id as string)}
        options={options}
        onChange={(values) => onChange({ ...parameters, [fieldId]: values })}
        placeholder={control.placeholder}
      />
    )
  }

  if (control.control === 'CustomSingleSelect') {
    const fieldId = control.id as keyof P
    const value = parameters[fieldId]
    const options = control.options!
    return (
      <CustomSingleSelect
        value={value as string}
        label={control.label || startCase(control.id as string)}
        options={options}
        onChange={(value) => onChange({ ...parameters, [fieldId]: value })}
        placeholder={control.placeholder}
        menuPlacement={control.menuPlacement}
      />
    )
  }

  if (control.control === 'Custom' && control.render) {
    return control.render(parameters, onChange)
  }

  if (control.control === 'Label') {
    return <Label label={control.label!} />
  }

  return control.control
}
